import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import React from 'react';
import { GameService } from '../services/gameService';
import { colors } from '../constants/colors';

function JoinGame() {
  const { roomId } = useParams();
  const [playerName, setPlayerName] = useState('');
  const [loading, setLoading] = useState(true);
  const [gameExists, setGameExists] = useState(false);
  const [gameData, setGameData] = useState(null);
  const [error, setError] = useState('');
  const [isJoining, setIsJoining] = useState(false);
  const navigate = useNavigate();
  
  const gameService = new GameService();

  useEffect(() => {
    const checkGameExists = async () => {
      try {
        const game = await gameService.getGame(roomId);
        
        if (game) {
          setGameData(game);
          setGameExists(true);
          
          const playerCount = Object.keys(game.players || {}).length;
          if (playerCount >= game.settings.maxPlayers) {
            setError('Game is full');
            setGameExists(false);
          }
          
          if (game.status === 'playing') {
            setError('Game already in progress');
            setGameExists(false);
          }
        } else {
          setGameExists(false);
          setError('Game not found');
        }
      } catch (error) {
        setGameExists(false);
        setError('Error connecting to game');
      } finally {
        setLoading(false);
      }
    };
    
    checkGameExists();
  }, [roomId]);

  const handleJoinGame = async () => {
    if (!playerName.trim()) {
      alert('Please enter your name');
      return;
    }

    setIsJoining(true);
    setError('');
    
    try {
      await gameService.joinGame(roomId, playerName.trim(), false);
      
      navigate(`/game/${roomId}`, { 
        state: { 
          playerName: playerName.trim(), 
          isHost: false,
          gameSettings: gameData?.settings || {}
        } 
      });
    } catch (error) {
      setError(error.message || 'Failed to join game');
      setIsJoining(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: colors.outerBackground,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          textAlign: 'center',
          background: colors.innerBackground,
          borderRadius: '15px',
          padding: '40px',
          color: colors.textOnDark
        }}>
          <h2>🔄 Checking game room...</h2>
          <p>Loading game: {roomId}</p>
        </div>
      </div>
    );
  }

  if (!gameExists) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: colors.outerBackground,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          textAlign: 'center',
          background: colors.innerBackground,
          borderRadius: '15px',
          padding: '40px',
          color: colors.textOnDark
        }}>
          <h2>❌ Cannot Join Game</h2>
          <p>{error || `Game room "${roomId}" doesn't exist.`}</p>
          <Link to="/" style={{
            backgroundColor: colors.accent,
            color: 'white',
            padding: '10px 20px',
            borderRadius: '8px',
            textDecoration: 'none'
          }}>
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: colors.outerBackground,
      padding: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{ maxWidth: '600px', width: '100%' }}>
        <Link to="/" style={{ 
          color: colors.textOnLight, 
          textDecoration: 'none',
          marginBottom: '20px',
          display: 'block'
        }}>
          ← Back to Home
        </Link>
        
        <h1 style={{ 
          textAlign: 'center', 
          color: colors.textOnLight,
          marginBottom: '30px'
        }}>
          Join Game
        </h1>
        
        <div style={{
          textAlign: 'center',
          background: colors.innerBackground,
          borderRadius: '10px',
          padding: '15px',
          margin: '20px 0',
          color: colors.textOnDark
        }}>
          <p>Game Code: <strong>{roomId}</strong></p>
        </div>
        
        {error && (
          <div style={{
            backgroundColor: colors.error,
            color: 'white',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}
        
        <div style={{
          background: colors.innerBackground,
          borderRadius: '15px',
          padding: '30px',
          color: colors.textOnDark
        }}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: colors.textOnDark 
            }}>
              Your Name:
            </label>
            <input
              type="text"
              placeholder="Enter your name"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !isJoining && handleJoinGame()}
              style={{
                width: '100%',
                padding: '12px',
                border: `2px solid ${colors.accent}`,
                borderRadius: '8px',
                fontSize: '16px',
                background: colors.inputBackground,
                color: colors.textOnLight,
                boxSizing: 'border-box'
              }}
              disabled={isJoining}
              maxLength="20"
            />
          </div>
          
          <button 
            onClick={handleJoinGame}
            disabled={isJoining || !playerName.trim()}
            style={{
              backgroundColor: isJoining || !playerName.trim() ? colors.buttonDisabled : colors.accent,
              color: 'white',
              border: 'none',
              padding: '16px 32px',
              fontSize: '18px',
              borderRadius: '8px',
              cursor: isJoining || !playerName.trim() ? 'not-allowed' : 'pointer',
              width: '100%',
              fontWeight: '600'
            }}
          >
            {isJoining ? '🔄 Joining...' : '🚀 Join Game'}
          </button>
        </div>
        
        {gameData && (
          <div style={{
            background: colors.innerBackground,
            borderRadius: '15px',
            padding: '20px',
            marginTop: '20px',
            color: colors.textOnDark
          }}>
            <h3>📋 Game Settings</h3>
            <p>Host: {gameData.host}</p>
            <p>Max Players: {gameData.settings?.maxPlayers || 10}</p>
            <p>Time per Question: {gameData.settings?.timePerQuestion || 120} seconds</p>
            <p>Total Questions: {gameData.settings?.totalQuestions || 10}</p>
            <p>Current Players: {Object.keys(gameData.players || {}).length}</p>
            <p>Status: {gameData.status}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default JoinGame;